import { useAppSelector } from '../app/hooks';
import { RootState } from '../app/store';

const TEAM_PLANS = ['sub_team_starter', 'sub_team_pro', 'sub_team_enterprise'] as const;

/**
 * Centralized access control hook.
 *
 * Three user tracks exist simultaneously:
 *  - isOneTime      → bought a physical NFC card (accountType: base|basic|business|corporate)
 *  - isSubscribed   → has an active recurring individual subscription
 *  - isTeamPlan     → has an active team subscription (sub_team_starter|pro|enterprise)
 *
 * Features are unlocked when ANY applicable track grants access.
 */
export const useAccess = () => {
    const { data: userData } = useAppSelector((state: RootState) => state.userMeta);

    const isOneTime = userData?.isOneTime === true;
    const accountType = userData?.accountType ?? '';
    const purchasedAdditionalLinks = (userData?.purchasedAdditionalLinks as number) ?? 0;

    // A subscription is only meaningful if it is active (not expired).
    const isSubscriptionActive = userData?.isSubscribed === true && !(isOneTime ? false : userData?.isExpired === true);
    const subPlan = userData?.subscriptionPlan ?? 'sub_free';

    const isFreePlan = isSubscriptionActive && subPlan === 'sub_free';
    const isTeamPlan = (TEAM_PLANS as readonly string[]).includes(subPlan) && isSubscriptionActive;

    // ─── One-time access levels ────────────────────────────────────────────────
    const oneTimeHasAnalytics = isOneTime && ['basic', 'business', 'corporate', 'enterprise'].includes(accountType);
    const oneTimeHasBusiness = isOneTime && ['business', 'corporate', 'enterprise'].includes(accountType);
    const oneTimeHasEnterprise = isOneTime && accountType === 'enterprise';

    // ─── Subscription access levels ────────────────────────────────────────────
    const subHasAnalytics = isSubscriptionActive && subPlan !== 'sub_free';
    // All team plans + sub_business/sub_premium grant business features
    const subHasBusiness = isSubscriptionActive && (
        ['sub_business', 'sub_premium'].includes(subPlan) || isTeamPlan
    );
    // sub_premium + team_pro/enterprise grant enterprise features
    const subHasEnterprise = isSubscriptionActive && (
        subPlan === 'sub_premium' ||
        subPlan === 'sub_team_pro' ||
        subPlan === 'sub_team_enterprise'
    );

    // ─── Merged access (any track grants access) ───────────────────────────────
    const hasAnalytics = oneTimeHasAnalytics || subHasAnalytics;
    const hasBusinessFeatures = oneTimeHasBusiness || subHasBusiness;
    const hasEnterpriseFeatures = oneTimeHasEnterprise || subHasEnterprise;

    // ─── Link creation limit ───────────────────────────────────────────────────
    // A user can hold a one-time package AND an active subscription at once;
    // the effective limit is the higher of the two, never just whichever track
    // happens to be checked first.
    const getSubscriptionLinkLimit = (): number | null => {
        if (isTeamPlan) {
            if (subPlan === 'sub_team_enterprise') return 100;
            if (subPlan === 'sub_team_pro') return 45;
            if (subPlan === 'sub_team_starter') return 25;
        }
        if (isSubscriptionActive) {
            if (subPlan === 'sub_premium') return 15;
            if (subPlan === 'sub_business') return 10;
            if (subPlan === 'sub_basic') return 3;
            return 1; // sub_free
        }
        return null;
    };

    const getOneTimeLinkLimit = (): number | null => {
        if (!isOneTime) return null;
        // Prefer the value stored in UserMeta (kept in sync by payment service);
        // fall back to computing from purchasedAdditionalLinks for older accounts.
        const storedMax = userData?.maxLinks as number | null | undefined;
        if (storedMax != null) return storedMax;
        if (['corporate', 'enterprise'].includes(accountType)) return 10 + purchasedAdditionalLinks;
        if (accountType === 'essential') return 10 + purchasedAdditionalLinks;
        if (accountType === 'business')  return 2  + purchasedAdditionalLinks;
        if (accountType === 'basic')     return 1  + purchasedAdditionalLinks;
        return null;
    };

    const linkLimit = Math.max(
        getSubscriptionLinkLimit() ?? 0,
        getOneTimeLinkLimit() ?? 0,
        1,
    );

    // ─── Wallet access ─────────────────────────────────────────────────────────
    // Add-to-Wallet requires an ACTIVE, non-free subscription (sub_basic and up).
    // A one-time-only user (no subscription) is excluded because isSubscriptionActive
    // already requires isSubscribed.
    const canUseWallet = isSubscriptionActive && subPlan !== 'sub_free';

    // ─── Free / restricted check ───────────────────────────────────────────────
    const isRestrictedFree = !isOneTime && (!isSubscriptionActive || isFreePlan);

    // ─── Admin access (determined server-side via /api/me) ──────────────────────
    const isAdmin = userData?.isAdmin === true;

    return {
        isAdmin,
        isOneTime,
        isSubscriptionActive,
        isFreePlan,
        isRestrictedFree,
        isTeamPlan,
        hasAnalytics,
        hasBusinessFeatures,
        hasEnterpriseFeatures,
        canUseWallet,
        linkLimit,
        accountType,
        subPlan,
    };
};
